// API 통신을 위한 유틸리티 함수들

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'  // 프로덕션에서는 상대 경로 사용 (Nginx가 프록시)
  : 'http://localhost:5000/api';

// 토큰 가져오기 (localStorage 또는 sessionStorage에서)
const getToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// API 요청 헬퍼 함수
const apiRequest = async (url, options = {}) => {
  const token = getToken();

  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
    cache: 'no-store', // 브라우저 캐시 사용 안 함
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '요청 처리 중 오류가 발생했습니다.');
  }

  return response.json();
};

// 로그인한 사용자의 매장 ID 가져오기
export const getStoreId = async () => {
  // 먼저 토큰에서 storeId를 가져옴
  const tokenStoreId = getStoreIdFromToken();
  if (tokenStoreId) {
    return tokenStoreId;
  }

  // 토큰이 없으면 localStorage에서 캐시된 storeId 확인
  const cachedStoreId = localStorage.getItem('currentStoreId');
  if (cachedStoreId) {
    return parseInt(cachedStoreId);
  }

  // 실패 시 기본값
  return 1;
};

// 토큰에서 store ID 가져오기 (동기 함수)
export const getStoreIdFromToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    // JWT 토큰 디코딩 (간단한 Base64 디코딩)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id || 1; // 토큰의 id 필드가 store_id
  } catch (error) {
    console.error('Failed to decode token:', error);
    return 1; // 기본값
  }
};

// 토큰에서 store_code 가져오기
export const getStoreCodeFromToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.storeCode || '3000000072415'; // 기본 storeCode
  } catch (error) {
    console.error('Failed to decode token:', error);
    return '3000000072415';
  }
};

// API 함수들
export { apiRequest };

// 생 fetch 로 관리자 API 를 부르는 곳에서 쓰는 인증 헤더.
// apiRequest 를 안 거치는 호출부가 많은데, 백엔드에 인증 가드가 붙은 뒤로는
// 이걸 빼먹으면 401 이 난다. Content-Type 은 호출부가 알아서 정한다
// (multipart 업로드는 브라우저가 boundary 를 붙여야 하므로 지정하면 안 됨).
export const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // 현재 storeId 가져오기
  getStoreId,

  // 매장 기본 정보 조회 (Redis 캐싱됨)
  getStoreInfo: async (storeId) => {
    const url = `/store-info/${storeId}`;
    return await apiRequest(url);
  },
  // 메뉴 조회
  // /api/store/:id/menus 가 백엔드에서 pos_type 자체 분기 (okdc / union / wowpos) 처리.
  // 호출처는 storeId 만 알면 됨 (posType 파라미터 불필요).
  getMenus: async (storeId = null, refresh = false) => {
    const targetStoreId = storeId || await getStoreId();
    const url = `/store/${targetStoreId}/menus${refresh ? '?refresh=true' : ''}`;
    return apiRequest(url);
  },

  // Union 동기화
  syncUnion: async (unionData) => {
    const storeCode = getStoreCodeFromToken(); // 로그인한 사용자의 storeCode 사용
    const storeId = await getStoreId(); // 현재 로그인한 store ID 사용
    return apiRequest('/sync-union', {
      method: 'POST',
      body: JSON.stringify({ storeCode, unionData, storeId }),
    });
  },

  // 메뉴 수정
  updateMenu: async (menuId, data) => {
    return apiRequest(`/menus/${menuId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // 품절 처리
  toggleSoldout: async (menuId, isSoldout) => {
    return apiRequest(`/menus/${menuId}/soldout`, {
      method: 'PATCH',
      body: JSON.stringify({ isSoldout }),
    });
  },

  // 주문 생성
  createOrder: async (tableCode, items) => {
    const storeId = await getStoreId();
    return apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify({ storeId, tableCode, items }),
    });
  },

  // 주문 조회
  getOrders: async (status, date) => {
    const storeId = await getStoreId();
    let url = `/orders/${storeId}`;
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (date) params.append('date', date);
    if (params.toString()) url += `?${params.toString()}`;

    return apiRequest(url);
  },

  // 주문 상태 변경
  updateOrderStatus: async (orderId, status) => {
    return apiRequest(`/orders/${orderId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },


  // 호출 사유 목록 조회 (다국어 지원) - 캐시 방지를 위한 타임스탬프 추가
  getCallReasons: async (language = 'ko') => {
    const storeId = await getStoreId();
    const timestamp = Date.now();
    return apiRequest(`/call-reasons/store/${storeId}?language=${language}&t=${timestamp}`);
  },

  // 호출 사유 목록 조회 (명시적 storeId 사용) - localStorage 오염 방지
  getCallReasonsWithStoreId: async (storeId, language = 'ko') => {
    const timestamp = Date.now();
    return apiRequest(`/call-reasons/store/${storeId}?language=${language}&t=${timestamp}`);
  },

  // 호출 사유 추가
  addCallReason: async (reason_text) => {
    const storeId = await getStoreId();
    return apiRequest(`/call-reasons/store/${storeId}`, {
      method: 'POST',
      body: JSON.stringify({ reason_text }),
    });
  },

  // 호출 사유 텍스트 업데이트 (언어별)
  updateCallReason: async (reasonId, language, text) => {
    const storeId = await getStoreId();
    return apiRequest(`/call-reasons/store/${storeId}/reason/${reasonId}`, {
      method: 'PUT',
      body: JSON.stringify({ language, text }),
    });
  },

  // 호출 사유 삭제
  deleteCallReason: async (reasonId) => {
    const storeId = await getStoreId();
    return apiRequest(`/call-reasons/store/${storeId}/reason/${reasonId}`, {
      method: 'DELETE',
    });
  },

  // itemCode로 메뉴 가격 조회
  getMenuPrices: async (itemCodes) => {
    const storeId = await getStoreId();
    const codesParam = itemCodes.join(',');
    return apiRequest(`/menu-prices/${storeId}?itemCodes=${codesParam}`);
  },

  // 카테고리 순서 업데이트
  updateCategoryOrder: async (categories) => {
    const storeId = await getStoreId();
    return apiRequest('/categories/reorder', {
      method: 'PUT',
      body: JSON.stringify({ storeId, categories }),
    });
  },

  // 호출 사유 순서 업데이트
  updateCallReasonsOrder: async (reasons, language = 'ko') => {
    const storeId = await getStoreId();
    return apiRequest(`/call-reasons/store/${storeId}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ reasons, language }),
    });
  },

  // OKDC 제품 ASP 실시간 품절 상태 조회
  getOKDCProductASPStatus: async (productId) => {
    return apiRequest(`/okdc/products/${productId}/asp-status`);
  },

  // 유니온포스 메뉴 순서 업데이트
  updateMenuOrder: async (storeId, categoryCode, menuOrders) => {
    return apiRequest('/menus/reorder', {
      method: 'PUT',
      body: JSON.stringify({ storeId, categoryCode, menuOrders }),
    });
  },

  // OKDC 메뉴 순서 업데이트
  updateOKDCMenuOrder: async (storeId, categoryCode, menuOrders) => {
    return apiRequest('/okdc/menus/reorder', {
      method: 'PUT',
      body: JSON.stringify({ storeId, categoryCode, menuOrders }),
    });
  },
};

export default api;